'use client';

import {
  BodyDefault,
  Button,
  IconButton,
  MetaDefault,
  Tooltip,
} from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { ViewIcon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type { AgentExecutionDto } from '@terra/shared-contracts-agent-execution';
import {
  formatDiscoveryDuration,
  getAgentDiscoveryHistoryDetailUrl,
  getAgentDiscoveryHistoryTitle,
  getAiDiscoveryMetadata,
} from './discovery.utils';
import type { McpDiscoveryRun } from './mcp-discovery-run.util';
import { AiDiscoveryStatusBadge } from './AiDiscoveryStatusBadge';

const timestampFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});

export function AiDiscoveryAgentHistoryRow({
  execution,
  showProtocol = true,
  onView,
  onRetry,
  onStop,
  isRetryPending,
}: {
  execution: AgentExecutionDto;
  showProtocol?: boolean;
  onView: () => void;
  onRetry?: () => void;
  onStop?: () => void;
  isRetryPending: boolean;
}) {
  const t = useTranslations('discovery.aiApplication');
  const metadata = getAiDiscoveryMetadata(execution.metadata);
  const protocol = showProtocol ? metadata?.targetProtocol : undefined;
  const title = getAgentDiscoveryHistoryTitle({ execution });
  const detailUrl = getAgentDiscoveryHistoryDetailUrl({ execution });
  const rollup = metadata?.verificationRollup;
  const metaLine = [
    protocol,
    rollup
      ? t('verificationRollupShort', {
          confirmed: rollup.confirmed,
          contradicted: rollup.contradicted,
          unverified: rollup.unverified,
        })
      : null,
    formatDiscoveryDuration({
      startedAt: execution.createdAt,
      completedAt: execution.completedAt,
      durationMs: execution.duration,
    }),
    timestampFormatter.format(new Date(execution.createdAt)),
  ]
    .filter(Boolean)
    .join(' · ');
  const tooltipTitle = [title, detailUrl, metaLine].filter(Boolean).join('\n');

  return (
    <HStack
      w="full"
      px="2"
      py="2.5"
      gap="3"
      alignItems="center"
      borderBottomWidth="1px"
      borderColor="border.muted"
      cursor="pointer"
      _hover={{ bg: 'bg.subtle' }}
      onClick={onView}
      overflow="hidden"
    >
      <Tooltip
        title={tooltipTitle}
        positioning={{ strategy: 'fixed', placement: 'top' }}
      >
        <VStack
          alignItems="flex-start"
          gap="0.5"
          minW="0"
          flex="1"
          overflow="hidden"
        >
          <HStack
            gap="2"
            alignItems="center"
            minW="0"
            w="full"
            overflow="hidden"
          >
            <Box flexShrink={0}>
              <AiDiscoveryStatusBadge status={execution.status} />
            </Box>
            <Box minW="0" flex="1" overflow="hidden">
              <BodyDefault truncate>{title}</BodyDefault>
            </Box>
          </HStack>
          <Box minW="0" w="full" maxW="100%" overflow="hidden">
            <MetaDefault
              color="fg.muted"
              truncate
              display="block"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {metaLine}
            </MetaDefault>
          </Box>
        </VStack>
      </Tooltip>
      <HStack
        gap="1"
        flexShrink={0}
        alignItems="center"
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
      >
        {onStop ? (
          <Button
            size="xs"
            variant="outline"
            colorPalette="red"
            onClick={onStop}
          >
            {t('stop')}
          </Button>
        ) : null}
        {onRetry ? (
          <Button
            size="xs"
            variant="outline"
            loading={isRetryPending}
            onClick={onRetry}
          >
            {t('retry')}
          </Button>
        ) : null}
        <IconButton
          size="xs"
          variant="ghost"
          aria-label={t('view')}
          onClick={onView}
        >
          <ViewIcon size={16} />
        </IconButton>
      </HStack>
    </HStack>
  );
}

export function AiDiscoveryMcpHistoryRow({
  run,
  onView,
}: {
  run: McpDiscoveryRun;
  onView: () => void;
}) {
  const t = useTranslations('discovery.aiApplication');

  return (
    <HStack
      w="full"
      px="2"
      py="2.5"
      gap="2"
      alignItems="center"
      borderBottomWidth="1px"
      borderColor="border.muted"
      cursor="pointer"
      _hover={{ bg: 'bg.subtle' }}
      onClick={onView}
    >
      <VStack alignItems="flex-start" gap="0.5" minW="0" flex="1">
        <HStack gap="2" alignItems="center">
          <AiDiscoveryStatusBadge status={run.status} />
          <BodyDefault truncate>
            {t('mcpRunTitle', {
              started: timestampFormatter.format(new Date(run.startedAt)),
            })}
          </BodyDefault>
        </HStack>
        <MetaDefault color="fg.muted">
          {t('mcpToolsProgress', {
            verified: run.verifiedProbeCount,
            total: run.probeCount,
          })}
          {' · '}
          {formatDiscoveryDuration({
            startedAt: run.startedAt,
            completedAt: run.completedAt,
            durationMs: run.durationMs,
          })}
        </MetaDefault>
      </VStack>
      <IconButton
        size="xs"
        variant="ghost"
        aria-label={t('view')}
        flexShrink={0}
        onClick={onView}
      >
        <ViewIcon size={16} />
      </IconButton>
    </HStack>
  );
}
